import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import OfficePropTypes from '../models/Office/OfficePropTypes';

import { media, Heading, InternalLink, Text, Card, colors, animation } from '../ui-components';

import { GatsbyImage, getImage } from 'gatsby-plugin-image';

const propTypes = {
  office: OfficePropTypes,
  secondary: PropTypes.bool
};

/**
 * Component that renders an office card
 *
 * @component OfficeCard
 * 
 * @param {object} props
 * 
 * @prop {object} office
 * @prop {boolean} secondary - determines which card version is displayed
 * 
 * @returns {JSX}
 */
const OfficeCard = props => {
  const { office, secondary = false } = props;
  const image = getImage(office?.thumbnail?.fluid);

  return (
    <OfficeCardContainer className={secondary ? 'secondary' : 'primary'}>
      {image && <OfficeIllustration image={image} alt={office?.thumbnail?.title || 'Office illustration'}/>}
      <WrapperLink to={office.slug ? `/about-us/our-offices/${office.slug}` : ''}>
        {secondary && <SecondaryOfficeTitle level={4}>{office.locationName}</SecondaryOfficeTitle>}
        <OfficeInfo>
          <OfficeTitle level={4}>{office.locationName}</OfficeTitle>
          <OfficeAddress>
            {office.address1}
            {office.address2 && <br />}
            {office.address2}
            <br />
            {office.citystatezip}
          </OfficeAddress>
          {office.phoneNumber && <OfficePhoneNumber>{office.phoneNumber}</OfficePhoneNumber>}
        </OfficeInfo>
        {office.slug && (
          <OfficeLink withArrow to={office.slug ? `/about-us/our-offices/${office.slug}` : ''}>
            {'View Office'}
          </OfficeLink>
        )}
      </WrapperLink>
    </OfficeCardContainer>
  );
};

const WrapperLink = styled(InternalLink)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  text-transform: none;
  letter-spacing: initial;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  flex: 1;
  padding: 28px 0 40px 28px;
  overflow: hidden;
`;

const OfficeIllustration = styled(GatsbyImage)`
  position: absolute;
  top: 200px;
  right: -40px;
  width: 250px;
  height: 200px;
  pointer-events: none; // allows to click through image
  scrollbar-width: none;
  transition: all ${animation.general.durationAndTiming};
`;

const OfficeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
`;

const OfficeTitle = styled(Heading)``;

const SecondaryOfficeTitle = styled(Heading)``;

const OfficeAddress = styled(Text)``;

const OfficePhoneNumber = styled(Text)``;

const OfficeLink = styled(InternalLink)`
  display: flex;
  z-index: 1;

  // Fixes visual issue on hover where arrow svg flashes white off hover, CSS needs the starting
  // color set on the SVG to transition color properly
  svg {
    color: ${colors.linkDefault};
  }
`;

const OfficeCardContainer = styled(Card)`
  position: relative;
  width: 100%;
  height: 400px;
  max-width: 411px;
  min-width: 335px;
  box-shadow: 0px 0px 80px 0px rgba(58, 58, 58, 0.06);
  overflow: hidden;

  ${OfficeTitle}, ${SecondaryOfficeTitle} {
    transition: color ${animation.general.durationAndTiming};
  }

  // Secondary card styles - !! Keep above hover state styles below
  &.secondary {
    max-width: 350px;

    ${WrapperLink} {
      align-items: center;
      padding: 69px 0 0 0;
    }

    ${OfficeIllustration} {
      position: absolute;
      top: 100px;
      right: 0;
      width: 350px;
      height: 300px;
    }

    ${OfficeInfo}, ${OfficeLink} {
      display: none;
    }

    @media screen and (max-width: ${media.smallTop}) {
      height: 225px;
      max-width: 100%;

      ${WrapperLink} {
        flex-direction: row;
        padding: 0 0 0 28px;
      }

      ${OfficeIllustration} {
        align-self: normal;
        width: 250px;
        height: 200px;
        top: 25px;
        right: -40px;
      }
    }
  }

  // Hover states are the same for both card styles *only* in desktop mode which is why some styling
  // here is duplicated from above
  &:hover {
    ${OfficeTitle}, ${SecondaryOfficeTitle} {
      color: ${colors.pureWhite};
    }

    ${OfficeLink} {
      text-decoration: underline;
    }

    @media screen and (min-width: ${media.smallTop}) {
      ${WrapperLink} {
        align-items: flex-start;
        padding: 28px 0 40px 28px;
      }

      ${OfficeIllustration} {
        position: absolute;
        top: 200px;
        right: -40px;
        width: 250px;
        height: 200px;
      }

      ${SecondaryOfficeTitle} {
        display: none;
      }

      ${OfficeInfo}, ${OfficeLink} {
        display: flex;
      }
    }
  }
`;

OfficeCard.propTypes = propTypes;

export default OfficeCard;
